import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { FiShoppingBag, FiTrash2, FiX } from "react-icons/fi";
import siteConfig, { formatCurrency } from "../../config/siteConfig";
import { useCommerce } from "../../hooks/useCommerce";
import Button from "../common/Button";
import EmptyState from "../common/EmptyState";
import { trapTabKey } from "../common/focusTrap";
import QuantityPicker from "../common/QuantityPicker";

const getImageSource = (image) => (typeof image === "string" ? image : image?.src || image?.url);
const EMPTY_ITEMS = [];

const CartDrawer = () => {
  const commerce = useCommerce();
  const closeButtonRef = useRef(null);
  const panelRef = useRef(null);
  const returnFocusRef = useRef(null);
  const items = useMemo(
    () => commerce.cartItems || commerce.cart || EMPTY_ITEMS,
    [commerce.cart, commerce.cartItems]
  );
  const open = Boolean(
    commerce.isCartOpen ?? commerce.isCartDrawerOpen ?? commerce.drawer?.cart
  );
  const closeCartAction = commerce.closeCart;
  const closeCartDrawer = commerce.closeCartDrawer;
  const closeDrawer = commerce.closeDrawer;
  const closeCart = useCallback(() => {
    if (closeCartAction) closeCartAction();
    else if (closeCartDrawer) closeCartDrawer();
    else closeDrawer?.("cart");
  }, [closeCartAction, closeCartDrawer, closeDrawer]);
  const updateQuantity = commerce.updateCartQuantity || commerce.updateQuantity;
  const removeItem = commerce.removeFromCart || commerce.removeCart;
  const freeSample = commerce.freeSample || "surprise";
  const subtotal = useMemo(
    () =>
      commerce.cartSubtotal ??
      commerce.subtotal ??
      items.reduce((total, item) => {
        const product = item.product || item;
        const variant = item.variant || {};
        return total + Number(variant.price ?? item.price ?? product.price ?? 0) * (Number(item.quantity) || 1);
      }, 0),
    [commerce.cartSubtotal, commerce.subtotal, items]
  );

  useEffect(() => {
    if (!open) return undefined;
    returnFocusRef.current = document.activeElement;
    document.body.classList.add("has-open-cart");
    closeButtonRef.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeCart?.();
      else trapTabKey(event, panelRef.current);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.classList.remove("has-open-cart");
      document.removeEventListener("keydown", handleKeyDown);
      returnFocusRef.current?.focus?.();
    };
  }, [closeCart, open]);

  if (!open) return null;

  const updateLine = (item, nextQuantity) => {
    const lineId = item.lineId || item.key || item.id;
    updateQuantity?.(lineId, nextQuantity);
  };

  const removeLine = (item) => {
    const lineId = item.lineId || item.key || item.id;
    removeItem?.(lineId);
  };

  return (
    <div className="cart-drawer" role="presentation">
      <button className="cart-drawer__backdrop" type="button" onClick={closeCart} aria-label="Close cart" />
      <aside ref={panelRef} className="cart-drawer__panel" role="dialog" aria-modal="true" aria-labelledby="cart-drawer-title" tabIndex="-1">
        <header className="cart-drawer__header">
          <div>
            <p className="cart-drawer__eyebrow">Your selections</p>
            <h2 id="cart-drawer-title">Shopping cart</h2>
          </div>
          <button ref={closeButtonRef} type="button" onClick={closeCart} aria-label="Close cart">
            <FiX aria-hidden="true" />
          </button>
        </header>

        {items.length === 0 ? (
          <EmptyState
            icon={FiShoppingBag}
            title="Your cart is empty"
            description="Explore the pantry and add something flavorful."
            actionLabel="Shop spices"
            actionTo={siteConfig.routes?.shop || "/shop"}
            onAction={closeCart}
          />
        ) : (
          <>
            <div className="cart-drawer__lines">
              <ul>
                {items.map((item) => {
                  const product = item.product || item;
                  const variant = item.variant || {};
                  const image = getImageSource(variant.image || product.images?.[0] || product.image);
                  const lineKey = item.lineId || item.key || `${product.id}-${variant.id || variant.name || "default"}`;
                  return (
                    <li className="cart-drawer__line" key={lineKey}>
                      <Link className="cart-drawer__image" to={`/products/${product.slug}`} onClick={closeCart}>
                        {image && <img src={image} alt="" loading="lazy" />}
                      </Link>
                      <div className="cart-drawer__line-content">
                        <div className="cart-drawer__line-heading">
                          <div>
                            <Link to={`/products/${product.slug}`} onClick={closeCart}>
                              {product.name}
                            </Link>
                            {(item.variantLabel || variant.label || variant.name || item.size) && (
                              <p>
                                {item.variantLabel || variant.label || variant.name || item.size}
                                {(item.variantWeight || variant.weight || variant.size) &&
                                  ` · ${item.variantWeight || variant.weight || variant.size}`}
                              </p>
                            )}
                          </div>
                          <button type="button" onClick={() => removeLine(item)} aria-label={`Remove ${product.name}`}>
                            <FiTrash2 aria-hidden="true" />
                          </button>
                        </div>
                        <div className="cart-drawer__line-controls">
                          <QuantityPicker
                            value={item.quantity || 1}
                            onChange={(quantity) => updateLine(item, quantity)}
                            max={variant.stock || 99}
                            label={`Quantity for ${product.name}`}
                          />
                          <strong>{formatCurrency(item.lineTotal ?? (variant.price ?? item.price ?? product.price) * (item.quantity || 1))}</strong>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="cart-drawer__footer">
              <label htmlFor="cart-free-sample">Choose a complimentary sample</label>
              <select
                id="cart-free-sample"
                value={freeSample}
                onChange={(event) => commerce.setFreeSample?.(event.target.value)}
              >
                <option value="surprise">Surprise me</option>
                <option value="smoky">Something smoky</option>
                <option value="bright">Something bright</option>
                <option value="warm">Something warm</option>
              </select>
              <div className="cart-drawer__subtotal">
                <span>Subtotal</span>
                <strong>{formatCurrency(subtotal)}</strong>
              </div>
              <p className="cart-drawer__shipping-note">
                Shipping and taxes are calculated at checkout.
              </p>
              <Button fullWidth to={siteConfig.routes?.checkout || "/checkout"} onClick={closeCart}>
                Secure checkout
              </Button>
              <Button fullWidth variant="secondary" to={siteConfig.routes?.cart || "/cart"} onClick={closeCart}>
                View cart
              </Button>
            </div>
          </>
        )}
      </aside>
    </div>
  );
};

export default CartDrawer;
